import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackClick, ClickEventName } from '../utils/analytics';
import { useAuthStore } from '../store/authStore';
import { initGoogleAuth, isGoogleAuthConfigured } from '../utils/googleAuth';

type ButtonVariant = 'dark' | 'light';
type ButtonSize = 'default' | 'large';

interface GoogleAuthButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  fullWidth?: boolean;
  text?: string;
  redirectTo?: string;
  trackingEvent?: ClickEventName;
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const GoogleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const GoogleAuthButton: React.FC<GoogleAuthButtonProps> = ({
  variant = 'dark',
  size = 'default',
  className = '',
  fullWidth = true,
  text = 'Sign up with Google',
  redirectTo = '/app/dashboard',
  trackingEvent = 'GoogleAuthButton_click',
}) => {
  const navigate = useNavigate();
  const { loginWithGoogleCode } = useAuthStore();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const codeClientRef = useRef<{ requestCode: () => void } | null>(null);
  const sdkReadyRef = useRef(false);

  const handleCodeResponse = useCallback(async (code: string) => {
    setSigningIn(true);
    setError(null);
    try {
      const result = await loginWithGoogleCode(code);
      navigate(result.needsOnboarding ? '/app/subscription' : redirectTo, { replace: true });
    } catch {
      setError('Google sign-in failed. Please try again.');
      setSigningIn(false);
    }
  }, [loginWithGoogleCode, navigate, redirectTo]);

  useEffect(() => {
    if (!isGoogleAuthConfigured()) return;

    const setup = async () => {
      try {
        await initGoogleAuth();
        codeClientRef.current = window.google!.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'openid email profile',
          ux_mode: 'popup',
          callback: (response: { code?: string; error?: string }) => {
            if (response.error) {
              setError('Google sign-in was cancelled.');
              setSigningIn(false);
              return;
            }
            if (response.code) {
              handleCodeResponse(response.code);
            }
          },
        });
        sdkReadyRef.current = true;
      } catch {
        console.error('Failed to initialize Google Sign-In SDK');
      }
    };

    setup();
  }, [handleCodeResponse]);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  const handleClick = () => {
    trackClick(trackingEvent);
    setError(null);

    if (!sdkReadyRef.current || !codeClientRef.current) {
      setError('Google Sign-In is loading, please try again.');
      return;
    }

    setSigningIn(true);
    codeClientRef.current.requestCode();
  };

  const baseStyles = 'inline-flex items-center justify-center gap-3 font-semibold transition-all duration-200 rounded-full';
  
  const sizeStyles = {
    default: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg',
  };

  const variantStyles = {
    dark: 'bg-slate-900 hover:bg-slate-800 text-white border border-slate-700 hover:border-slate-600 shadow-lg hover:shadow-xl',
    light: 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-300 hover:border-gray-400 shadow-md hover:shadow-lg',
  };

  const widthStyle = fullWidth ? 'w-full' : '';
  const disabledStyles = signingIn ? 'opacity-60 cursor-not-allowed' : '';

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={handleClick}
        disabled={signingIn}
        className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${widthStyle} ${disabledStyles} ${className}`}
      >
        {signingIn ? (
          <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <GoogleIcon className="w-5 h-5" />
        )}
        <span>{signingIn ? 'Signing in...' : text}</span>
      </button>
      {error && (
        <p className="mt-2 text-sm text-red-500 text-center">{error}</p>
      )}
    </div>
  );
};

export default GoogleAuthButton;
